import { CaretRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { CSSProperties, memo, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { areEqual } from 'react-window';
import styled from 'styled-components';

import { ItemInventory } from 'apis/inventory-management-service/types';
import { Category, Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined, localize } from 'common/utils';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';

import AllDisabledCategoryActions from './AllDisabledCategoryActions';
import { ItemDragOverlay } from './ItemDragOverlay';
import { MenuEditorDnDItem } from './MenuEditorDnDContext';
import { MenuFeaturesContext } from './MenuFeaturesContext';
import { MenuLocaleContext } from '../MenuLocaleContext';
import CategoryActions from '../categories/components/CategoryActions';
import { MENU_MOBILE_BREAKPOINT } from '../constants';
import CategorySubheader from '../items/components/CategorySubheader';
import ItemHeader from '../items/components/Item/ItemHeader';
import DragOverlayItemHeader from '../items/components/Item/ItemHeader/DragOverlayItemHeader';
import { transformItemInventoryToQuantity } from '../items/components/Item/utils';
import OptionHeader from '../options/components/OptionHeader';
import { MenuEditorElement } from '../types';
import { isMenuEditorItem } from '../utils';

export type RowRendererData = {
  list: MenuEditorElement[];
  menuId: string;
  itemInventory?: ItemInventory[];
  activeItemId?: string;
  activeOptionId?: string;
  shouldRenderAllDisabledActions?: boolean;
  disableDraggingSpecials?: boolean;
  selectedIds?: Set<string>;
  canDrop?: boolean;
  canDrag?: boolean;
  draggingId?: string;
  onSelectItems?: (ids: string[]) => void;
  onDeselectItems?: (ids: string[]) => void;
  onEnableFail?: (item: Item) => void;
  onEnableSuccess?: (item: Item) => void;
  onItemClick?: (itemId: string) => void;
  onOptionClick?: (optionId: string) => void;
  onItemCopy?: (itemId: string, isSpecial: boolean) => void;
  onOptionCopy?: (optionId: string) => void;
};

type Props = {
  index: number;
  data: RowRendererData;
  style: CSSProperties;
};

const StyledItemHeader = styled(ItemHeader)<{ isDragging?: boolean }>`
  border: none;
  min-width: 550px;
  opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }
`;

const StyledOptionHeader = styled(OptionHeader)`
  min-width: 550px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }
`;

const StyledCategorySubheader = styled(CategorySubheader)`
  min-width: 550px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    min-width: 320px;
  }
`;

const ItemRow = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledCaretRight = styled(CaretRight)`
  width: 14px;
  margin-inline-start: 16px;
  margin-inline-end: 16px;
  fill: ${cbSemanticCssVars.textPrimary};
`;

const getSelectionStatus = ({ itemIds, selectedIds }: { selectedIds?: Set<string>; itemIds: string[] }) =>
  [
    !itemIds.length || !selectedIds?.size ? ('not-selected' as const) : undefined,
    itemIds.every(id => selectedIds?.has(id)) ? ('selected' as const) : undefined,
    itemIds.some(id => selectedIds?.has(id)) ? ('partially-selected' as const) : undefined,
    'not-selected' as const,
  ].filter(isDefined)[0];

const MenuEditorListRowRenderer = ({
  index,
  data: {
    list,
    menuId,
    itemInventory,
    activeItemId,
    activeOptionId,
    shouldRenderAllDisabledActions,
    canDrop,
    canDrag,
    draggingId,
    disableDraggingSpecials = false,
    selectedIds,
    onSelectItems,
    onDeselectItems,
    onEnableFail,
    onEnableSuccess,
    onItemClick,
    onOptionClick,
    onItemCopy,
    onOptionCopy,
  },
  style,
}: Props) => {
  const { pathname } = useLocation();
  const { isRetail } = useContext(MenuFeaturesContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const rowData = useMemo(() => list[index], [list, index]);

  const getItemIdsByCategory = useCallback(
    (categoryId?: string) => {
      const itemList = list.filter(isMenuEditorItem);
      const itemsByCategory = categoryId
        ? itemList.filter(item => item.category?.id === categoryId)
        : itemList.filter(item => !item.category);
      return itemsByCategory.map(item => item.itemState.id);
    },
    [list],
  );

  const itemQuantity = useMemo(() => {
    if (rowData.type === 'item') {
      const inventory = itemInventory?.find(inventory => inventory.itemId === rowData.itemState.id);
      return inventory ? transformItemInventoryToQuantity(inventory) : undefined;
    }
    return undefined;
  }, [rowData, itemInventory]);

  const getRenderedActions = useCallback(
    (category: Category | null, type: 'category' | 'subcategory') => {
      if (!category) {
        return undefined;
      }
      if (shouldRenderAllDisabledActions) {
        return <AllDisabledCategoryActions category={category} />;
      }
      if (type === 'subcategory') {
        return <CategoryActions category={category} menuId={menuId} type="subcategory" />;
      }

      const canAddSubcategory = category.item_bindings.length === 0 && isRetail;

      return (
        <CategoryActions category={category} menuId={menuId} enableAddSubcategory={canAddSubcategory} type="category" />
      );
    },
    [isRetail, menuId, shouldRenderAllDisabledActions],
  );

  const handleSelectItem = useCallback(
    (itemId: string) => {
      onSelectItems?.([itemId]);
    },
    [onSelectItems],
  );

  const handleDeselectItem = useCallback(
    (itemId: string) => {
      onDeselectItems?.([itemId]);
    },
    [onDeselectItems],
  );

  const handleItemClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>, itemId: string) => {
      if (event.metaKey || event.ctrlKey) {
        event.stopPropagation();
      } else {
        onItemClick?.(itemId);
      }
    },
    [onItemClick],
  );

  if (rowData.type === 'category-subheader') {
    return (
      <ItemRow style={style}>
        <StyledCategorySubheader
          actions={getRenderedActions(rowData.category, 'category')}
          header={
            rowData.category ? (
              <>
                {rowData.parentCategory && (
                  <>
                    {localize(rowData.parentCategory.name, primaryLanguage)}
                    <StyledCaretRight />
                  </>
                )}
                {localize(rowData.category.name, primaryLanguage)}
              </>
            ) : (
              <FormattedMessage id="menu.nonCategorised" />
            )
          }
          disabledSelection={!!activeItemId}
          onDeselect={onDeselectItems ? () => onDeselectItems(getItemIdsByCategory(rowData.category?.id)) : undefined}
          onSelect={onSelectItems ? () => onSelectItems(getItemIdsByCategory(rowData.category?.id)) : undefined}
          selectionStatus={
            getItemIdsByCategory(rowData.category?.id).every(id => selectedIds?.has(id))
              ? 'selected'
              : getItemIdsByCategory(rowData.category?.id).some(id => selectedIds?.has(id))
                ? 'partially-selected'
                : 'not-selected'
          }
        />
      </ItemRow>
    );
  }
  if (rowData.type === 'options-subheader') {
    return (
      <ItemRow style={style}>
        <StyledCategorySubheader header={rowData.name} />;
      </ItemRow>
    );
  }
  if (rowData.type === 'option') {
    return (
      <ItemRow style={style}>
        <a href={`${pathname}?option=${rowData.optionState.id}`} onClick={e => e.preventDefault()}>
          <StyledOptionHeader
            active={rowData.optionState.id === activeOptionId}
            hideVat
            onCopy={onOptionCopy}
            onClick={event => {
              if (event.metaKey || event.ctrlKey) {
                event.stopPropagation();
              } else {
                onOptionClick?.(rowData.optionState.id);
              }
            }}
            option={rowData.optionState}
            orderDisabledChoicesFirst
          />
        </a>
      </ItemRow>
    );
  }
  if (rowData.type === 'item' && disableDraggingSpecials && rowData.category?.id === WOLT_SPECIALS_CATEGORY_ID) {
    return (
      <ItemRow style={style}>
        <StyledItemHeader
          active={rowData.itemState.id === activeItemId}
          category={rowData.category}
          item={rowData.itemState}
          itemInventoryCount={itemQuantity}
          disabledSelection={!!activeItemId}
          onSelect={handleSelectItem}
          onDeselect={handleDeselectItem}
          onEnableFail={onEnableFail && (() => onEnableFail(rowData.itemState))}
          onEnableSuccess={onEnableSuccess && (() => onEnableSuccess(rowData.itemState))}
          selected={selectedIds?.has(rowData.itemState.id)}
          onCopy={onItemCopy}
          onClick={handleItemClick}
        />
      </ItemRow>
    );
  }

  if (rowData.type === 'subcategory-subheader') {
    const { category } = rowData;
    const itemIds = getItemIdsByCategory(category?.id);

    return (
      <ItemRow style={style}>
        <StyledCategorySubheader
          actions={getRenderedActions(category, 'subcategory')}
          header={category ? localize(category.name, primaryLanguage) : <FormattedMessage id="menu.nonCategorised" />}
          disabledSelection={!!activeItemId || !category?.item_bindings.length}
          onDeselect={() => onDeselectItems?.(itemIds)}
          onSelect={() => onSelectItems?.(itemIds)}
          selectionStatus={getSelectionStatus({
            itemIds,
            selectedIds,
          })}
        />
      </ItemRow>
    );
  }

  if (rowData.type === 'item') {
    const isBulkDragging =
      !!draggingId &&
      selectedIds &&
      selectedIds.has(draggingId) &&
      selectedIds.has(rowData.itemState.id) &&
      selectedIds.size > 1;

    const hasCategory = !!rowData.category;

    return (
      <ItemRow style={style}>
        <MenuEditorDnDItem
          id={rowData.itemState.id}
          disabled={{
            droppable: !canDrop,
            draggable: !canDrag,
          }}
          data={
            selectedIds && selectedIds.size > 1 && selectedIds.has(rowData.itemState.id)
              ? {
                  type: hasCategory ? 'DND_EDITOR_MULTIPLE_ITEMS' : 'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
                  selectedIds,
                  dragOverlay: <ItemDragOverlay count={selectedIds.size} />,
                }
              : {
                  type: hasCategory ? 'DND_EDITOR_SINGLE_ITEM' : 'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
                  dragOverlay: (
                    <DragOverlayItemHeader
                      category={rowData.category}
                      item={rowData.itemState}
                      itemInventoryCount={itemQuantity}
                      selected={selectedIds?.has(rowData.itemState.id)}
                    />
                  ),
                }
          }
        >
          {({ isDragging }) => (
            <StyledItemHeader
              isDragging={isBulkDragging || isDragging}
              active={rowData.itemState.id === activeItemId}
              category={rowData.category}
              item={rowData.itemState}
              itemInventoryCount={itemQuantity}
              disabledSelection={!!activeItemId}
              onSelect={handleSelectItem}
              onDeselect={handleDeselectItem}
              onEnableFail={onEnableFail && (() => onEnableFail(rowData.itemState))}
              onEnableSuccess={onEnableSuccess && (() => onEnableSuccess(rowData.itemState))}
              selected={selectedIds?.has(rowData.itemState.id)}
              onCopy={onItemCopy}
              onClick={handleItemClick}
            />
          )}
        </MenuEditorDnDItem>
      </ItemRow>
    );
  }
  return null;
};

export default memo(MenuEditorListRowRenderer, areEqual);
